// @flow
import React, { useState, useEffect, useContext, useCallback } from 'react';
import styled, { css } from 'styled-components';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import TextField from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import { gridSize, fontSize } from '@atlaskit/theme';
import Select, { type OptionsType, FormatOptionLabelMeta } from '@atlaskit/select';
import SectionMessage from '@atlaskit/section-message';
import InlineEdit from '@atlaskit/inline-edit';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Spinner from '@atlaskit/spinner';
import { SimpleTag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Tick from '@atlaskit/icon/glyph/check-circle';
import { isEqual } from 'lodash';
import { compose, graphql, WithApolloClient, withApollo } from 'react-apollo';
import Avatar from '@atlaskit/avatar';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { VIRTUAL_COMPONENT_QUERY } from '../../graphql/queries';
import { JIRA_USERS_FOR_PICKER, UPDATE_VIRTUAL_COMPONENT } from '../../graphql/mutations';
import { ErrorParser } from '../../utils/ErrorParser';
import { ASSIGNEE_TYPES, ASSIGNEE_TYPES_OBJECT } from '../assets/assigneeTypes.assets';
import { compareJiraProjects } from '../../utils/compareJiraProjects';
import { analytics } from '../../services/analytics';
import type { VirtualComponent } from '../../types';
import ProjectWarningModal from './ProjectWarningModal';
import { ALL_PROJECTS_OPTION, OCTO_GLOBAL_PERMISSIONS, ERROR_CODES, RED, GREEN } from '../../utils/consts';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import { useJiraUsersProvider } from '../../contexts/JiraUsersContext';
import CustomAsyncSelect from '../select/CustomAsyncSelect';
import VirtualComponentSelect, {
    parseVirtualComponentToOption,
    parseVirtualComponentsToOptions,
} from '../select/VirtualComponentSelect';
import LabelSelect, { parseLabelToOptions } from '../select/LabelSelect';
import ProjectSelect, { parseProjectsToOptions, parseProjectsToOptionsGroups } from '../select/ProjectSelect';
import { getJiraProjects } from '../../services/jira-services';
import VirtualComponentTabs from './VirtualComponentTabs';

const Wrapper = styled.div`
    padding: 14px;
    height: 100vh;
    overflow-y: auto;
    box-sizing: border-box;
`;

const NameView = styled.h1`
    font-size: 2rem;
    font-weight: 500;
    padding-left: 6px;
    ${(props) =>
        props.error &&
        css`
            color: #f44336;
        `}
`;

const NameInput = styled.div`
    input {
        margin-bottom: 1px;
        font-size: 2rem;
        letter-spacing: -0.01em;
        font-weight: 500;
        box-sizing: border-box;
        padding: 6px;
        padding-top: 7px;
    }
`;

const DefaultView = styled.div`
    line-height: ${(gridSize() * 2.5) / fontSize()};
    padding: ${gridSize() - 2}px ${gridSize() - 2}px;
    word-break: break-word;
    cursor: text;
    white-space: pre-wrap;

    span.projects-select > div > div:nth-child(2) {
        padding-top: 4px;
        padding-bottom: 4px;
        margin-top: -4px;
        margin-left: -4px;
        width: calc(100% + 8px);
        height: calc(100% + 8px);
    }

    span.assignee-select > div {
        margin-top: -8px;
        margin-left: -10px;
    }

    ${(props) =>
        props.isEmpty &&
        css`
            color: #bbb;
        `}
    ${(props) =>
        props.error &&
        css`
            color: #f44336;
        `}
`;

const ComponentLeadView = styled.div`
    p {
        margin-left: 7px;
        margin-top: 0;
    }
    > * {
        display: inline-block;
        vertical-align: middle;
    }
`;

const ButtonView = styled.div`
    margin-bottom: 14px;
    text-align: right;

    > * {
        display: inline-block;
    }

    button {
        background-color: transparent;
    }
`;

const selectStylesBase = {
    menu: (base) => ({ ...base, zIndex: 2 }),
};

const PROJECTS_TO_EDIT_PROJECTS = (projects) =>
    projects
        .map((project) => ({
            value: project.jiraProjectId,
            label: project.jiraProjectName,
            obj: project,
        }))
        .sort((a, b) => {
            return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
        });

const EDIT_PROJECTS_TO_PROJECTS = (editProjects) =>
    editProjects.map((editProject) => ({
        ...editProject.obj,
        jiraProjectName: editProject.label,
        jiraProjectId: editProject.value,
        jiraAvatarUrls: {
            large: `https://swanlydemo.atlassian.net/secure/projectavatar?size=large&s=large&pid=${editProject.value}`,
        },
    }));

const PROJECTS_TO_SERVER = (projects) =>
    projects.map((project) => {
        return {
            id: project.obj?.jiraProjectId ?? project.obj?.id ?? project.jiraProjectId ?? project.id,
            name: project.obj?.jiraProjectName ?? project.obj?.name ?? project.jiraProjectName ?? project.name,
            key: project.obj?.jiraProjectKey ?? project.obj?.key ?? project.jiraProjectKey ?? project.key,
            jiraComponentId: project.obj?.jiraComponentId ?? project.jiraComponentId,
        };
    });

const PROJECTS_TO_CREATE_TO_SERVER = (projects) =>
    projects.map((project) => {
        return {
            id: project.jiraProjectId ?? project.id,
            name: project.jiraProjectName ?? project.name,
            key: project.jiraProjectKey ?? project.key,
        };
    });

const PROJECTS_TO_REMOVE_TO_SERVER = (projects) =>
    projects.map((project) => {
        return {
            id: project.id,
            name: project.jiraProjectName,
            key: project.jiraProjectKey,
            jiraProjectId: project.jiraProjectId,
        };
    });

const parseAssigneeTypeToOption = (assigneeType) => {
    return assigneeType ? { value: assigneeType, label: ASSIGNEE_TYPES_OBJECT[assigneeType] } : ASSIGNEE_TYPES[0];
};

const parseUserToOption = (user) => {
    return user && user?.jiraUserAccountId !== 'unknown'
        ? {
              value: user.accountId ?? user.jiraUserAccountId,
              label: user.displayName ?? user.jiraUserDisplayName,
              avatarUrl: user.avatarUrl || user.jiraUserAvatarUrl || user.avatarUrls?.['48x48'],
          }
        : null;
};

let labelArray = [];

type Props = {
    virtualComponentBeingEdited: VirtualComponent,
    editVirtualComponent: Function,
    handleSetEdited: Function,
    handleSetFlag: Function,
    setToDelete: Function,
    setToMerge: Function,
    synchronizeComponents: Function,
    skipVirtualLabelsQueryRefetch?: boolean,
    skipVirtualComponentsQueryRefetch?: boolean,
    skipVirtualComponentQueryRefetch?: boolean,
    componentParentsDefaultOptions?: OptionsType[],
    componentLeadDefaultOptions?: OptionsType[],
    componentProjectsDefaultOptions?: OptionsType[],

    /* props from compose */
    getUsersForPicker: Function,
    virtualComponentQuery: any,
    updateVirtualComponent: Function,
};

const MoreIconTrigger = (params) => {
    const { triggerRef, ...props } = params;
    return <IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />;
};
function VirtualComponentEdit({
    virtualComponentBeingEdited,
    editVirtualComponent,
    handleSetEdited,
    handleSetFlag,
    setToDelete,
    setToMerge,
    synchronizeComponents,
    skipVirtualComponentQueryRefetch,
    componentParentsDefaultOptions,
    componentLeadDefaultOptions,
    componentProjectsDefaultOptions,

    /* props from compose */
    getUsersForPicker,
    virtualComponentQuery,
    updateVirtualComponent,
}: WithApolloClient<Props>) {
    const [virtualComponent, setVirtualComponent] = useState(virtualComponentBeingEdited);
    const { usersCache, setUsersCache } = useJiraUsersProvider();

    // state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedLabels, setSelectedLabels] = useState([]);

    const [projects, setProjects] = useState(virtualComponent ? virtualComponent.projects : []);
    const [editProjects, setEditProjects] = useState(projects ? PROJECTS_TO_EDIT_PROJECTS(projects) : []);

    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedAssigneeType, setSelectedAssigneeType] = useState(ASSIGNEE_TYPES[0]);
    const [selectedParentComponents, setSelectedParentComponents] = useState([]);

    const [projectsMenuIsOpen, setProjectsMenuIsOpen] = useState(false);
    const [isProjectsWarningModalOpen, setIsProjectsWarningModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const permissions = useContext(OctoPermissionsContext);

    useEffect(() => {
        if (virtualComponentQuery?.virtualComponent) {
            setVirtualComponent(virtualComponentQuery?.virtualComponent);
        }
    }, [virtualComponentQuery?.virtualComponent]);

    const [isProjectsLoading, setIsProjectsLoading] = useState(false);

    const _setEditProjects = useCallback((newProjects) => {
        setIsProjectsLoading(true);
        if (newProjects.some((p) => p.value === ALL_PROJECTS_OPTION)) {
            getJiraProjects({ maxParallelRequests: 20 }).then((projectsList) => {
                setEditProjects(parseProjectsToOptions(projectsList));
                setProjectsMenuIsOpen(false);
                setIsProjectsLoading(false);
            });
        } else {
            setEditProjects(newProjects);
            setIsProjectsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!virtualComponent) {
            return;
        }
        setName(virtualComponent.name);
        setDescription(virtualComponent.description);
        setSelectedLabels(parseLabelToOptions(virtualComponent.virtualLabels));

        setProjects(virtualComponent.projects);
        setEditProjects(PROJECTS_TO_EDIT_PROJECTS(virtualComponent.projects));

        setSelectedLead(parseUserToOption(virtualComponent.virtualComponentLead));

        setSelectedAssigneeType(parseAssigneeTypeToOption(virtualComponent.assigneeType));
        setSelectedParentComponents(parseVirtualComponentsToOptions(virtualComponent.parents));
    }, [virtualComponent]);

    const toggleWarningModal = (value: boolean): void => {
        setIsProjectsWarningModalOpen(value);
    };

    const onCancelEdit = useCallback(() => {
        // this forces the reload to previous values
        setVirtualComponent((prev) => ({ ...prev }));
    }, []);

    // submit form
    const _confirmChanges = (field: string, value: Any): void => {
        setError('');

        const input = {
            id: virtualComponent.id,
            field,
            name: virtualComponent.name,
            description: virtualComponent.description,
            assigneeType: virtualComponent.assigneeType,
            componentLead: virtualComponent.componentLead,
            projects: [],
            virtualLabels: [],
            projectsToCreate: [],
            projectsToRemove: [],
        };

        switch (field) {
            case 'name': {
                if (!value) return setName(virtualComponent.name);
                if (value === virtualComponent.name) {
                    return undefined;
                }
                input.name = value;
                setName(value);
                break;
            }

            case 'description': {
                if (value === virtualComponent.description) {
                    return undefined;
                }
                input.description = value;
                setDescription(value);
                break;
            }
            case 'labels': {
                if (
                    isEqual(
                        (selectedLabels ?? []).map(({ id }) => id),
                        (virtualComponent.virtualLabels ?? []).map(({ id }) => id),
                    )
                ) {
                    return undefined;
                }
                input.virtualLabels = selectedLabels.map((label) => ({
                    id: label.id || 'new',
                    name: label.label,
                }));
                break;
            }
            case 'projects': {
                const { create, remove } = compareJiraProjects(value, projects);
                if (!create.length && !remove.length) return undefined;
                if (!create.length && !remove.length) {
                    return undefined;
                }
                input.projects = PROJECTS_TO_SERVER(projects);
                input.projectsToCreate = PROJECTS_TO_CREATE_TO_SERVER(create);
                input.projectsToRemove = PROJECTS_TO_REMOVE_TO_SERVER(remove);
                setProjects(value);
                break;
            }

            case 'componentLead': {
                if (!value) {
                    input.componentLead = null;
                    break;
                }

                if (value?.value === virtualComponent.virtualComponentLead?.jiraUserAccountId) {
                    return undefined;
                }
                input.componentLead = {
                    jiraUserAccountId: value.value,
                    jiraUserDisplayName: value.label,
                    jiraUserAvatarUrl: value.avatarUrl,
                };
                break;
            }

            case 'assigneeType': {
                if (value === virtualComponent.assigneeType) {
                    return undefined;
                }
                input.assigneeType = value;
                break;
            }

            case 'parents': {
                if (
                    isEqual(
                        value ?? [],
                        (virtualComponent.parents ?? []).map(({ id }) => id),
                    )
                ) {
                    return undefined;
                }
                input.parentIds = value;
                break;
            }

            default: {
                input[field] = value;
            }
        }

        setLoading(true);
        const refetchQueries = [];

        if (!skipVirtualComponentQueryRefetch) {
            refetchQueries.push({ query: VIRTUAL_COMPONENT_QUERY, variables: { id: virtualComponent.id } });
        }
        updateVirtualComponent({
            variables: { input },
            refetchQueries,
        })
            .then((res) => {
                handleSetEdited(true);
                const { updated, loopParents } = res.data.updateVirtualComponent;

                if (updated.virtualLabels?.length > 0) {
                    labelArray = updated.virtualLabels.map((item) => ({
                        id: item.id,
                        label: item.name,
                        value: item.name.toLowerCase().replace(/\W/g, ''),
                    }));
                }
                setVirtualComponent(updated);
                if (loopParents?.length) {
                    handleSetFlag({
                        icon: <WarningIcon primaryColor={RED} />,
                        title: `Warning`,
                        description: (
                            <span>
                                {`You can't select these components as a parent based on your current hierarchy setup: ${loopParents
                                    .map((parent) => parent.name)
                                    .join(', ')}.`}
                                &nbsp;
                                <a
                                    href="https://apps.appf.re/oc/doc/hierarchy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Learn More
                                </a>
                            </span>
                        ),
                        key: 'Warning',
                        autoDismiss: true,
                    });
                } else {
                    handleSetFlag({
                        icon: <Tick primaryColor={GREEN} />,
                        title: 'Component updated successfully.',
                        key: 'success',
                    });
                }
                analytics.track({
                    userId: window.license,
                    event: 'updated_component',
                    timestamp: new Date(),
                    properties: {
                        addon_key: process.env.REACT_APP_KEY,
                        change_type: field,
                        has_labels: labelArray.length > 1 ? 'true' : 'false',
                    },
                });
            })
            .catch((e) => {
                console.log('full error:', e);

                onCancelEdit();
                if (e && e.message && e.message.indexOf(ERROR_CODES.SYNC_IN_PROGRESS) !== -1) {
                    setError(
                        'A synchronization process is running. Please try again in a few seconds after synchronization process is done.',
                    );
                } else if (e && e.message && e.message.indexOf(ERROR_CODES.IMPORT_IN_PROGRESS) !== -1) {
                    setError(
                        'A import process is running. Please try again in a few seconds after import process is done.',
                    );
                } else if (ErrorParser.parse(e.message) !== 'Unable to update component') {
                    setError(e.message || 'Please try again later');
                } else {
                    handleSetFlag({
                        icon: <ErrorIcon primaryColor={RED} />,
                        title: "We couldn't finish the update",
                        description:
                            "This usually happens when components are updated from Jira and aren't synchronized yet. If this problem persist contact support on https://apps.appf.re/oc/support/p2",
                        key: 'error',
                        actions: [{ content: 'Synchronize components', onClick: synchronizeComponents }],
                        autoDismiss: false,
                    });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (selectedLead?.value && (!selectedLead?.label || !selectedLead?.avatarUrl)) {
            const userFromCache = usersCache[selectedLead?.value];

            if (!userFromCache) {
                setUsersCache((prev) => ({
                    ...prev,
                    [selectedLead?.value]: prev[selectedLead?.value] ?? undefined,
                }));
                return;
            }

            setSelectedLead(parseUserToOption(userFromCache));
        }
    }, [selectedLead?.avatarUrl, selectedLead?.label, selectedLead?.value, setUsersCache, usersCache]);

    const _handleSearchUsers = async (value) => {
        if (value) {
            try {
                const res = await getUsersForPicker({ variables: { value: encodeURI(value) } });

                const { jiraUsers } = res.data;

                const mapped = jiraUsers.map((user) => ({
                    value: user.accountId,
                    label: user.displayName,
                    avatarUrl: user.avatarUrl,
                }));

                setUsersCache((prev) => ({
                    ...mapped.reduce(
                        (acc, curr) => {
                            if (curr.value) {
                                return {
                                    ...acc,
                                    [curr.value]: {
                                        accountId: curr.value,
                                        displayName: curr.label,
                                        avatarUrl: curr.avatarUrl,
                                    },
                                };
                            }
                            return acc;
                        },
                        { ...prev },
                    ),
                }));

                return mapped;
            } catch (err) {
                console.log('full error:', err);
                setError(err.message || 'Please try again later');
                return [];
            }
        } else {
            return [];
        }
    };

    const _checkProjects = (newEditProjects: Array): Function => {
        if (!newEditProjects.length) {
            onCancelEdit();
            return;
        }

        const projectsTo = EDIT_PROJECTS_TO_PROJECTS(newEditProjects);

        const { remove } = compareJiraProjects(projectsTo, projects);
        if (remove.length) {
            setIsProjectsWarningModalOpen(true);
        } else {
            _confirmChanges('projects', projectsTo);
        }
    };

    const hasPermissionToEdit = () => hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions);

    const formatUserOptionLabel = (
        option?: { label: String, value: String, avatarUrl: string },
        { context }: FormatOptionLabelMeta,
    ) => {
        if (context === 'menu') {
            return (
                <ComponentLeadView>
                    <Avatar size="small" src={option?.avatarUrl} />
                    <p>{option?.label || 'Unassigned'}</p>
                </ComponentLeadView>
            );
        }

        return option?.label || 'Unassigned';
    };

    if (virtualComponentQuery.loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    padding: '15px 0',
                }}
            >
                <p>Loading component...</p>
                <Spinner size="medium" />
            </div>
        );
    }

    return (
        <Wrapper>
            {isProjectsWarningModalOpen && (
                <ProjectWarningModal
                    setIsOpen={toggleWarningModal}
                    confirm={() => _confirmChanges('projects', EDIT_PROJECTS_TO_PROJECTS(editProjects))}
                    cancel={onCancelEdit}
                />
            )}

            <ButtonView>
                {loading && <Spinner size="small" />}
                {hasPermissionToEdit() && (
                    <DropdownMenu trigger={MoreIconTrigger} position="bottom right">
                        <DropdownItemGroup>
                            <DropdownItem onClick={() => setToMerge(virtualComponent)}>Merge</DropdownItem>
                            <DropdownItem onClick={() => setToDelete(virtualComponent)}>Delete</DropdownItem>
                        </DropdownItemGroup>
                    </DropdownMenu>
                )}
                <Button iconBefore={<CrossIcon />} onClick={() => editVirtualComponent(null)}></Button>
            </ButtonView>

            {error && (
                <SectionMessage appearance="error">
                    <p>There was an error editing the component:</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{ErrorParser.parse(error)}</p>
                </SectionMessage>
            )}

            {/* name */}
            <InlineEdit
                defaultValue={name}
                readViewFitContainerWidth
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <NameInput>
                        <TextField {...fieldProps} autoFocus />
                    </NameInput>
                )}
                readView={() => (
                    <DefaultView style={{ paddingLeft: 0 }}>
                        <NameView error={!name}>{name || 'Insert a name for this component'}</NameView>
                    </DefaultView>
                )}
                onConfirm={(value) => _confirmChanges('name', value)}
                onCancel={onCancelEdit}
            />

            {/* description */}
            <InlineEdit
                defaultValue={description}
                readViewFitContainerWidth
                label="Description"
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps, ref) => <TextArea ref={ref} {...fieldProps} autoFocus />}
                readView={() => <DefaultView isEmpty={!description}>{description || 'Empty description'}</DefaultView>}
                onConfirm={(value) => _confirmChanges('description', value)}
                onCancel={onCancelEdit}
            />

            {/* labels */}
            <InlineEdit
                readViewFitContainerWidth
                defaultValue={selectedLabels}
                label="Component labels"
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <LabelSelect
                        {...fieldProps}
                        creatable
                        isMulti
                        autoFocus
                        openMenuOnFocus
                        closeMenuOnSelect={false}
                        value={selectedLabels}
                        onChange={setSelectedLabels}
                        defaultOptions
                    />
                )}
                readView={() =>
                    selectedLabels.length === 0 ? (
                        <DefaultView isEmpty>
                            {hasPermissionToEdit() ? 'Search for component label' : 'No component label selected'}
                        </DefaultView>
                    ) : (
                        <div style={{ padding: `${gridSize() / 2}px` }}>
                            <TagGroup>
                                {selectedLabels.map((option) => (
                                    <SimpleTag text={option.label} key={option.label} />
                                ))}
                            </TagGroup>
                        </div>
                    )
                }
                onConfirm={() => _confirmChanges('labels')}
                onCancel={onCancelEdit}
            />

            {/* Projects */}
            <InlineEdit
                label="Projects"
                readViewFitContainerWidth
                isRequired
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <ProjectSelect
                        {...fieldProps}
                        isMulti
                        autoFocus
                        openMenuOnFocus
                        closeMenuOnSelect={false}
                        menuIsOpen={projectsMenuIsOpen}
                        value={editProjects}
                        onChange={_setEditProjects}
                        defaultOptions={
                            componentProjectsDefaultOptions?.length
                                ? parseProjectsToOptionsGroups(componentProjectsDefaultOptions)
                                : true
                        }
                        includeAddAll
                        onMenuOpen={() => setProjectsMenuIsOpen(true)}
                        onMenuClose={() => setProjectsMenuIsOpen(false)}
                        isLoading={isProjectsLoading}
                    />
                )}
                readView={() =>
                    editProjects.length ? (
                        <DefaultView>
                            <TagGroup>
                                {editProjects.map((project) => (
                                    <SimpleTag
                                        key={project.value}
                                        text={project.label}
                                        elemBefore={<Avatar size="xsmall" src={project?.obj?.jiraAvatarUrls?.large} />}
                                    />
                                ))}
                            </TagGroup>
                        </DefaultView>
                    ) : (
                        <DefaultView error>Select at least one project</DefaultView>
                    )
                }
                onConfirm={() => _checkProjects(editProjects)}
                onCancel={onCancelEdit}
            />

            {/* Component lead */}
            <InlineEdit
                readViewFitContainerWidth
                label="Component lead"
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <CustomAsyncSelect
                        {...fieldProps}
                        autoFocus
                        openMenuOnFocus
                        isClearable={true}
                        placeholder="Type user name"
                        loadOptions={_handleSearchUsers}
                        value={selectedLead}
                        onChange={setSelectedLead}
                        defaultOptions={
                            componentLeadDefaultOptions?.length
                                ? componentLeadDefaultOptions.map(parseUserToOption)
                                : true
                        }
                        formatOptionLabel={formatUserOptionLabel}
                        filterOption={() => {
                            return true;
                        }}
                    />
                )}
                readView={() =>
                    selectedLead ? (
                        <DefaultView>
                            <ComponentLeadView>
                                <Avatar size="small" src={selectedLead.avatarUrl} />
                                <p>{selectedLead.label}</p>
                            </ComponentLeadView>
                        </DefaultView>
                    ) : (
                        <DefaultView isEmpty>No default component lead</DefaultView>
                    )
                }
                onConfirm={() => _confirmChanges('componentLead', selectedLead)}
                onCancel={onCancelEdit}
            />

            {/* default assignee */}
            <InlineEdit
                label="Default assignee"
                readViewFitContainerWidth
                isRequired
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <Select
                        {...fieldProps}
                        autoFocus
                        openMenuOnFocus
                        options={ASSIGNEE_TYPES}
                        value={selectedAssigneeType}
                        onChange={setSelectedAssigneeType}
                        styles={selectStylesBase}
                    />
                )}
                readView={() =>
                    selectedAssigneeType ? (
                        <DefaultView>{selectedAssigneeType.label}</DefaultView>
                    ) : (
                        <DefaultView error>No default assignee selected</DefaultView>
                    )
                }
                onConfirm={() => _confirmChanges('assigneeType', selectedAssigneeType.value)}
                onCancel={onCancelEdit}
            />

            {/* parent components */}
            <InlineEdit
                label="Parent components"
                readViewFitContainerWidth
                isEditing={hasPermissionToEdit() ? undefined : false}
                editView={(fieldProps) => (
                    <VirtualComponentSelect
                        {...fieldProps}
                        isMulti
                        autoFocus
                        openMenuOnFocus
                        closeMenuOnSelect={false}
                        value={selectedParentComponents}
                        onChange={setSelectedParentComponents}
                        defaultOptions={
                            componentParentsDefaultOptions?.length
                                ? componentParentsDefaultOptions
                                      ?.filter(({ id }) => id !== virtualComponent.id)
                                      .map(parseVirtualComponentToOption)
                                : true
                        }
                        excludeComponentIds={[virtualComponent.id]}
                        menuPlacement="top"
                    />
                )}
                readView={() =>
                    !selectedParentComponents?.length ? (
                        <DefaultView isEmpty>No parent components selected</DefaultView>
                    ) : (
                        <div style={{ padding: `${gridSize() / 2}px` }}>
                            <TagGroup>
                                {selectedParentComponents?.map((parent) => (
                                    <SimpleTag text={parent.label} key={parent.value} />
                                ))}
                            </TagGroup>
                        </div>
                    )
                }
                onConfirm={() => {
                    const parentIds = selectedParentComponents.map((p) => p.value);
                    _confirmChanges('parents', parentIds);
                }}
                onCancel={onCancelEdit}
            />
            <br />
            <VirtualComponentTabs type="component" virtualComponentOrVersion={virtualComponent} />
        </Wrapper>
    );
}

export default compose(
    withApollo,
    graphql(JIRA_USERS_FOR_PICKER, { name: 'getUsersForPicker' }),
    graphql(VIRTUAL_COMPONENT_QUERY, {
        name: 'virtualComponentQuery',
        options: (props) => ({
            variables: {
                id: props.virtualComponentBeingEdited.id,
            },
        }),
    }),
    graphql(UPDATE_VIRTUAL_COMPONENT, { name: 'updateVirtualComponent' }),
)(VirtualComponentEdit);
